import { useState, useEffect } from 'react'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog'
import { toast } from "sonner"
import { useTranslation } from "@/hooks/use-translation"
import { GameLayout, GameStatusAlert } from '@/components/games/common'
import { GameShortcuts } from '@/components/games/common/game-shortcuts'
import { Keyboard } from 'lucide-react'
import { getDailyWord, getDictionary, getDateString, removeAccents } from '@/lib/dictionaries'
import { saveGameResult, getGameResult, hasPlayedToday } from '@/lib/game-stats'
import { InContentAd } from '@/components/adsense'

interface GameState {
  attempts: string[]
  currentAttempt: string[]  // Cambiar a array para manejar posiciones fijas
  gameStatus: 'playing' | 'won' | 'lost'
  currentRow: number
  selectedCell: { row: number; col: number }
  isShaking: boolean
  isFlipping: boolean
  revealingRow: number | null
  revealingLetter: number | null
  revealedLetters: Set<number>
}

interface LetterState {
  letter: string
  state: 'correct' | 'present' | 'absent' | 'empty'
}

export function AdivinaLaPalabra() {
  const { t, language } = useTranslation()
  const [gameState, setGameState] = useState<GameState>({
    attempts: [],
    currentAttempt: ['', '', '', '', ''],  // Array de 5 posiciones vacías
    gameStatus: 'playing',
    currentRow: 0,
    selectedCell: { row: 0, col: 0 },
    isShaking: false,
    isFlipping: false,
    revealingRow: null,
    revealingLetter: null,
    revealedLetters: new Set<number>()
  })

  // Nuevo estado para controlar si estamos en modo "nuevo juego"
  const [, setIsNewGameMode] = useState(false)

  // Estado para controlar el modal de primera vez en móvil
  const [showFirstTimeModal, setShowFirstTimeModal] = useState(false)

  // Flag para evitar guardado automático durante la carga inicial
  const [isInitialLoadComplete, setIsInitialLoadComplete] = useState(false)

  // Obtener Adivina la Palabra y diccionario según el idioma actual
  const today = getDateString()
  // Crear fecha local explícitamente para evitar problemas de zona horaria
  const [year, month, day] = today.split('-').map(Number)
  const todayDate = new Date(year, month - 1, day) // month - 1 porque los meses van de 0-11
  const targetWord = getDailyWord(language, todayDate)
  const dictionary = getDictionary(language)
  const maxAttempts = 6
  const wordLength = 5



  // Función para guardar el estado parcial del juego
  const savePartialGameState = (state: GameState) => {
    if (state.gameStatus === 'playing') {
      const partialState = {
        attempts: state.attempts,
        currentAttempt: state.currentAttempt,
        currentRow: state.currentRow,
        selectedCell: state.selectedCell,
        date: today
      }
      localStorage.setItem(`game-adivina-la-palabra-${language}-partial`, JSON.stringify(partialState))
    }
  }

  // Función para cargar el estado parcial del juego
  const loadPartialGameState = (): Partial<GameState> | null => {
    const key = `game-adivina-la-palabra-${language}-partial`
    const stored = localStorage.getItem(key)
    if (stored) {
      try {
        const parsed = JSON.parse(stored)
        // Verificar que la fecha guardada coincida con el día actual
        if (parsed.date === today) {
          return parsed
        } else {
          // Si la fecha no coincide, limpiar el estado parcial
          localStorage.removeItem(key)
          return null
        }
      } catch (error) {
        return null
      }
    } else {
      return null
    }
  }

  // Función para limpiar el estado parcial (cuando se completa el juego)
  const clearPartialGameState = () => {
    const key = `game-adivina-la-palabra-${language}-partial`
    localStorage.removeItem(key)
  }

  useEffect(() => {
    // Añadir estilos CSS para animaciones específicas del juego
    const style = document.createElement('style')
    style.textContent = `
      @keyframes shake {
        0%, 100% { transform: translateX(0); }
        10%, 30%, 50%, 70%, 90% { transform: translateX(-5px); }
        20%, 40%, 60%, 80% { transform: translateX(5px); }
      }

      @keyframes pop {
        0% { transform: scale(1); }
        50% { transform: scale(1.1); }
        100% { transform: scale(1.05); }
      }
    `
    document.head.appendChild(style)

    return () => {
      document.head.removeChild(style)
    }
  }, [])

  useEffect(() => {
    // Reiniciar flag de carga inicial
    setIsInitialLoadComplete(false)

    // Verificar si ya se jugó hoy
    if (hasPlayedToday(`adivina-la-palabra-${language}`, today)) {
      const todayResult = getGameResult(`adivina-la-palabra-${language}`, today)
      if (todayResult) {
        setGameState({
          attempts: todayResult.attempts,
          currentAttempt: ['', '', '', '', ''],  // Array vacío para partidas completadas
          gameStatus: todayResult.won ? 'won' : 'lost',
          currentRow: todayResult.attempts.length,
          selectedCell: { row: todayResult.attempts.length, col: 0 },
          isShaking: false,
          isFlipping: false,
          revealingRow: null,
          revealingLetter: null,
          revealedLetters: new Set<number>()
        })
        // Limpiar estado parcial si ya se completó el juego
        clearPartialGameState()
      }
    } else {
      // Intentar cargar estado parcial si no se ha completado el juego
      const partialState = loadPartialGameState()
      if (partialState) {
        setGameState(prev => ({
          ...prev,
          attempts: partialState.attempts || [],
          currentAttempt: partialState.currentAttempt || ['', '', '', '', ''],
          currentRow: partialState.currentRow || 0,
          selectedCell: partialState.selectedCell || { row: 0, col: 0 }
        }))
      }
    }

    // Verificar si es la primera vez en móvil
    const hasSeenInstructions = localStorage.getItem(`game-adivina-la-palabra-${language}-instructions`)
    if (!hasSeenInstructions && window.innerWidth < 1024) { // lg breakpoint
      setShowFirstTimeModal(true)
    }

    // Marcar que la carga inicial está completa
    setIsInitialLoadComplete(true)
  }, [language, today])

  // Efecto para guardar el estado parcial cuando cambie el gameState
  useEffect(() => {
    if (!isInitialLoadComplete) {
      return
    }

    if (gameState.gameStatus === 'playing') {
      savePartialGameState(gameState)
    }
  }, [gameState, isInitialLoadComplete])

  // Reset shaking animation
  useEffect(() => {
    if (gameState.isShaking) {
      const timer = setTimeout(() => {
        setGameState(prev => ({ ...prev, isShaking: false }))
      }, 600)
      return () => clearTimeout(timer)
    }
  }, [gameState.isShaking])

  const getLetterState = (letter: string, position: number): LetterState['state'] => {
    const targetLetter = targetWord[position]
    const accentFreeLetter = removeAccents(letter)

    if (accentFreeLetter === targetLetter) {
      return 'correct'
    } else if (targetWord.includes(accentFreeLetter)) {
      return 'present'
    } else {
      return 'absent'
    }
  }

  const evaluateAttempt = (attempt: string): LetterState[] => {
    const result: LetterState[] = attempt.split('').map((letter) => ({
      letter,
      state: 'absent' as LetterState['state']
    }))

    // Crear contadores de letras en la palabra objetivo (sin acentos)
    const targetLetterCounts: { [key: string]: number } = {}
    for (const letter of targetWord) {
      targetLetterCounts[letter] = (targetLetterCounts[letter] || 0) + 1
    }

    // Primer paso: marcar todas las letras correctas (verde) - sin acentos
    for (let i = 0; i < attempt.length; i++) {
      const accentFreeLetter = removeAccents(attempt[i])
      if (accentFreeLetter === targetWord[i]) {
        result[i].state = 'correct'
        targetLetterCounts[accentFreeLetter]-- // Reducir disponibilidad
      }
    }

    // Segundo paso: marcar letras presentes pero en posición incorrecta (naranja) - sin acentos
    for (let i = 0; i < attempt.length; i++) {
      if (result[i].state !== 'correct') { // Solo procesar si no es correcta
        const accentFreeLetter = removeAccents(attempt[i])
        if (targetLetterCounts[accentFreeLetter] && targetLetterCounts[accentFreeLetter] > 0) {
          result[i].state = 'present'
          targetLetterCounts[accentFreeLetter]-- // Reducir disponibilidad
        }
      }
    }

    return result
  }

  const submitAttempt = () => {
    const currentWord = gameState.currentAttempt.join('')

    if (currentWord.length !== wordLength) {
      toast.error(t("games.adivinaLaPalabra.wordTooShort"))
      return
    }

    // Validar palabra sin acentos
    const accentFreeCurrentWord = removeAccents(currentWord)
    if (!dictionary.validWords.includes(accentFreeCurrentWord)) {
      setGameState(prev => ({ ...prev, isShaking: true }))
      toast.error(t("games.adivinaLaPalabra.invalidWord"))
      return
    }

    const isCorrect = accentFreeCurrentWord === targetWord
    const isGameOver = gameState.currentRow >= maxAttempts - 1

    // Añadir la palabra a attempts y activar animación (sin acentos)
    const newAttempts = [...gameState.attempts, accentFreeCurrentWord]

    setGameState(prev => ({
      ...prev,
      attempts: newAttempts,
      isFlipping: true,
      revealingRow: prev.currentRow
    }))

    // Guardar inmediatamente el estado con la nueva línea añadida
    const intermediateState = {
      ...gameState,
      attempts: newAttempts,
      currentAttempt: ['', '', '', '', ''], // Limpiar current attempt
      currentRow: gameState.currentRow + 1,
      selectedCell: { row: gameState.currentRow + 1, col: 0 }
    }
    savePartialGameState(intermediateState)

    // Guardar resultado INMEDIATAMENTE si el juego termina
    if (isCorrect || isGameOver) {
      // Guardar resultado
      saveGameResult(`adivina-la-palabra-${language}`, newAttempts, isCorrect, today)
      // Resetear modo nuevo juego al completar una partida
      setIsNewGameMode(false)
      // Limpiar estado parcial al completar el juego
      clearPartialGameState()

      if (isCorrect) {
        toast.success(t("games.adivinaLaPalabra.congratulations"), {
          duration: 4000
        })
      } else {
        toast.error(t("games.adivinaLaPalabra.gameOver", { word: targetWord }), {
          duration: 4000
        })
      }
    }

    // Después de 1.2 segundos (600ms última carta + 600ms animación), completar
    setTimeout(() => {
      setGameState(prev => ({
        ...prev,
        currentAttempt: ['', '', '', '', ''],
        gameStatus: isCorrect ? 'won' : (isGameOver ? 'lost' : 'playing'),
        currentRow: prev.currentRow + 1,
        selectedCell: { row: prev.currentRow + 1, col: 0 },
        isFlipping: false,
        revealingRow: null,
        revealingLetter: null,
        revealedLetters: new Set<number>()
      }))
    }, 1200)
  }



  const handleKeyPress = (key: string) => {
    if (gameState.gameStatus !== 'playing') return
    if (gameState.selectedCell.row !== gameState.currentRow) return
    if (gameState.isFlipping) return // Bloquear input durante animaciones

    const selectedCol = gameState.selectedCell.col

    if (key === 'CHECK') {
      submitAttempt()
    } else if (key === 'BACKSPACE') {
      const newAttempt = [...gameState.currentAttempt]
      let newSelectedCol = selectedCol

      // Si la casilla actual está vacía, ir a la anterior y borrarla
      if (newAttempt[selectedCol] === '' && selectedCol > 0) {
        newSelectedCol = selectedCol - 1
        newAttempt[newSelectedCol] = ''
      } else {
        // Si la casilla actual tiene letra, borrarla y quedarse ahí
        newAttempt[selectedCol] = ''
        newSelectedCol = selectedCol
      }

      const newState = {
        ...gameState,
        currentAttempt: newAttempt,
        selectedCell: { row: gameState.selectedCell.row, col: newSelectedCol }
      }

      setGameState(prev => ({
        ...prev,
        currentAttempt: newAttempt,
        selectedCell: { row: prev.selectedCell.row, col: newSelectedCol }
      }))

      // Guardar inmediatamente
      savePartialGameState(newState)
    } else if (key.length === 1 && key.match(/[A-ZÑ]/)) {
      // Escribir en la posición seleccionada
      if (selectedCol >= 0 && selectedCol < wordLength) {
        const newAttempt = [...gameState.currentAttempt]
        newAttempt[selectedCol] = key

        // Solo mover al siguiente si NO estamos en la última posición (4)
        const newSelectedCol = selectedCol < wordLength - 1 ? selectedCol + 1 : selectedCol

        const newState = {
          ...gameState,
          currentAttempt: newAttempt,
          selectedCell: { row: gameState.selectedCell.row, col: newSelectedCol }
        }

        setGameState(prev => ({
          ...prev,
          currentAttempt: newAttempt,
          selectedCell: { row: prev.selectedCell.row, col: newSelectedCol }
        }))

        // Guardar inmediatamente
        savePartialGameState(newState)
      }
    }
  }

  const getLetterColor = (state: LetterState['state']) => {
    switch (state) {
      case 'correct': return 'bg-primary text-primary-foreground border-primary'
      case 'present': return 'bg-secondary text-secondary-foreground border-secondary'
      case 'absent': return 'bg-gray-500 text-white border-gray-500'
      default: return 'bg-white border-gray-300 text-gray-900 dark:bg-gray-800 dark:border-gray-600 dark:text-gray-100'
    }
  }

  const getKeyColor = (state: string) => {
    switch (state) {
      case 'correct': return 'bg-primary text-primary-foreground'
      case 'present': return 'bg-secondary text-secondary-foreground'
      case 'absent': return 'bg-gray-500 text-white border-gray-500'
      default: return 'bg-gray-200 hover:bg-gray-300 text-gray-800 dark:bg-gray-600 dark:hover:bg-gray-500 dark:text-gray-100'
    }
  }

  const handleCloseFirstTimeModal = () => {
    setShowFirstTimeModal(false)
    localStorage.setItem(`game-adivina-la-palabra-${language}-instructions`, 'true')
  }

  const renderRow = (attempt: string, rowIndex: number) => {
    const isCurrentRow = rowIndex === gameState.currentRow && gameState.gameStatus === 'playing'
    const isRevealingRow = gameState.revealingRow === rowIndex && gameState.isFlipping
    const isShakingRow = isCurrentRow && gameState.isShaking

    // Para la fila actual, usar el array currentAttempt
    // Para filas completadas, usar el string attempt
    const letters = isCurrentRow ? gameState.currentAttempt : attempt.split('')

    const letterStates = attempt ? evaluateAttempt(attempt) : []

    return (
      <div
        key={rowIndex}
        className={`flex ${gameState.gameStatus === 'playing' ? 'gap-2' : 'gap-1'} justify-center ${isShakingRow ? 'animate-[shake_0.6s_ease-in-out]' : ''}`}
      >
        {Array.from({ length: wordLength }, (_, letterIndex) => {
          const letter = letters[letterIndex] || ''
          const state = letterStates[letterIndex]?.state || 'empty'
          const isSelected = gameState.selectedCell.row === rowIndex && gameState.selectedCell.col === letterIndex
          const isSelectable = isCurrentRow && gameState.gameStatus === 'playing'
          const hasLetter = letter !== ''
          const isFlipping = isRevealingRow && gameState.isFlipping

          // Una fila está "revelada" si ya tiene un attempt (está en gameState.attempts)
          const isRevealed = attempt && attempt.length > 0

          // Delay secuencial: 0ms, 150ms, 300ms, 450ms, 600ms
          const flipDelay = letterIndex * 150

          return (
            <div
              key={letterIndex}
              className={`${gameState.gameStatus === 'playing' ? 'w-12 h-12 lg:w-16 lg:h-16' : 'w-8 h-8 lg:w-12 lg:h-12'} relative`}
              style={{ perspective: '1000px' }}
              onClick={() => {
                if (isSelectable) {
                  setGameState(prev => ({
                    ...prev,
                    selectedCell: { row: rowIndex, col: letterIndex }
                  }))
                }
              }}
            >
              {/* Card Flipper Container */}
              <div className="relative w-full h-full" style={{ transformStyle: 'preserve-3d' }}>
                {/* Front Face - Color original */}
                <div
                  className={`absolute inset-0 border-2 rounded-lg flex items-center justify-center font-bold uppercase cursor-pointer bg-white border-gray-300 text-gray-900 dark:bg-gray-800 dark:border-gray-600 dark:text-gray-100 ${gameState.gameStatus === 'playing' ? 'text-lg lg:text-2xl' : 'text-sm lg:text-lg'} ${isSelected && isSelectable ? 'ring-2 ring-blue-500 ring-offset-1' : ''
                    } ${isSelectable ? 'hover:bg-gray-100 dark:hover:bg-gray-700' : ''} ${hasLetter && isCurrentRow ? 'animate-[pop_0.15s_ease-in-out] scale-105' : ''
                    }`}
                  style={{
                    backfaceVisibility: 'hidden',
                    transform: (isFlipping || isRevealed) ? `rotateX(180deg)` : 'rotateX(0deg)',
                    transition: isFlipping ? `transform 0.6s ${flipDelay}ms ease-out` : 'none',
                    transformStyle: 'preserve-3d',
                    zIndex: 2
                  }}
                >
                  {letter}
                </div>

                {/* Back Face - Color final */}
                <div
                  className={`absolute inset-0 border-2 rounded-lg flex items-center justify-center font-bold uppercase cursor-pointer ${getLetterColor(state)} ${gameState.gameStatus === 'playing' ? 'text-lg lg:text-2xl' : 'text-sm lg:text-lg'} ${gameState.gameStatus === 'won' && rowIndex === gameState.currentRow - 1 ? 'animate-[bounce_0.6s_ease-in-out_3]' : ''
                    }`}
                  style={{
                    backfaceVisibility: 'hidden',
                    transform: (isFlipping || isRevealed) ? `rotateX(360deg)` : 'rotateX(180deg)',
                    transition: isFlipping ? `transform 0.6s ${flipDelay}ms ease-in` : 'none',
                    transformStyle: 'preserve-3d',
                    zIndex: 2
                  }}
                >
                  {letter}
                </div>
              </div>
            </div>
          )
        })}
      </div>
    )
  }

  // Teclado adaptado según el idioma
  const getKeyboard = () => {
    switch (language) {
      case 'es':
        return [
          ['Q', 'W', 'E', 'R', 'T', 'Y', 'U', 'I', 'O', 'P'],
          ['A', 'S', 'D', 'F', 'G', 'H', 'J', 'K', 'L', 'Ñ'],
          ['CHECK', 'Z', 'X', 'C', 'V', 'B', 'N', 'M', 'BACKSPACE']
        ]
      case 'fr':
        return [
          ['A', 'Z', 'E', 'R', 'T', 'Y', 'U', 'I', 'O', 'P'],
          ['Q', 'S', 'D', 'F', 'G', 'H', 'J', 'K', 'L', 'M'],
          ['CHECK', 'W', 'X', 'C', 'V', 'B', 'N', 'BACKSPACE']
        ]
      default: // 'en'
        return [
          ['Q', 'W', 'E', 'R', 'T', 'Y', 'U', 'I', 'O', 'P'],
          ['A', 'S', 'D', 'F', 'G', 'H', 'J', 'K', 'L'],
          ['CHECK', 'Z', 'X', 'C', 'V', 'B', 'N', 'M', 'BACKSPACE']
        ]
    }
  }

  const keyboard = getKeyboard()

  const getKeyState = (key: string) => {
    const usedLetters = gameState.attempts.join('').split('').map(letter => removeAccents(letter))
    const accentFreeKey = removeAccents(key)
    if (!usedLetters.includes(accentFreeKey)) return 'unused'

    // Verificar si la letra está en la palabra objetivo (sin acentos)
    for (const attempt of gameState.attempts) {
      for (let i = 0; i < attempt.length; i++) {
        const accentFreeAttemptLetter = removeAccents(attempt[i])
        if (accentFreeAttemptLetter === accentFreeKey) {
          const state = getLetterState(key, i)
          if (state === 'correct') return 'correct'
          if (state === 'present') return 'present'
        }
      }
    }
    return 'absent'
  }

  const renderGameContent = () => (
    <>
      {/* Game Status - Show above the game board */}
      {gameState.gameStatus !== 'playing' && (
        <GameStatusAlert status={gameState.gameStatus}>
          <div className="space-y-1">
            <div className="font-bold">
              {gameState.gameStatus === 'won'
                ? t("games.adivinaLaPalabra.youWon", { attempts: gameState.attempts.length })
                : t("games.adivinaLaPalabra.youLost", { word: targetWord })
              }
            </div>
            <div className="text-xs font-medium">
              {t("games.adivinaLaPalabra.comeBackTomorrow")}
            </div>
          </div>
        </GameStatusAlert>
      )}

      {/* Game Board */}
      <div className="space-y-2 mb-6">
        {Array.from({ length: maxAttempts }, (_, index) => {
          const attempt = gameState.attempts[index] || ''
          return renderRow(attempt, index)
        })}
      </div>
    </>
  )



  return (
    <GameLayout>
      {/* Desktop Layout */}
      <div className="hidden lg:flex lg:flex-row lg:gap-8 lg:px-8 lg:py-8">
        <div className="flex-1 flex flex-col justify-center items-center">
          <div className="w-full max-w-xl xl:max-w-2xl">
            {renderGameContent()}

            {/* Desktop Virtual Keyboard - Only show when playing */}
            {gameState.gameStatus === 'playing' && (
              <div className="space-y-1 w-full max-w-3xl mx-auto">
                {keyboard.map((row, rowIndex) => (
                  <div key={rowIndex} className="flex gap-2 justify-center">
                    {row.map((key) => {
                      const state = key.length === 1 ? getKeyState(key) : 'unused'
                      const isCheckKey = key === 'CHECK'
                      const isBackspaceKey = key === 'BACKSPACE'

                      return (
                        <Button
                          key={key}
                          variant="outline"
                          size="sm"
                          className={`min-w-14 h-16 p-1 text-lg font-bold rounded-lg ${getKeyColor(state)} ${isCheckKey ? 'bg-primary hover:bg-primary/90 text-primary-foreground border-primary min-w-20' : ''
                            } ${isBackspaceKey ? 'min-w-20' : ''}`}
                          onClick={() => handleKeyPress(key)}
                          disabled={gameState.gameStatus !== 'playing' || (isCheckKey && gameState.currentAttempt.join('').length !== wordLength)}
                        >
                          {isCheckKey ? '✓' : isBackspaceKey ? '⌫' : key}
                        </Button>
                      )
                    })}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Sidebar - Desktop only - Siempre mostrar reglas */}
        <div className="w-80 min-w-80 space-y-4">
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="flex items-center gap-2 text-sm">
                <Keyboard className="h-4 w-4" />
                {t("games.adivinaLaPalabra.howToPlay")}
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-2">
              <p className="text-xs">{t("games.adivinaLaPalabra.rules.main")}</p>
              <div className="space-y-1">
                <div className="flex items-center gap-2">
                  <Badge className="bg-primary text-primary-foreground text-xs">{t("games.adivinaLaPalabra.rules.correct")}</Badge>
                  <span className="text-xs">{t("games.adivinaLaPalabra.rules.correctDesc")}</span>
                </div>
                <div className="flex items-center gap-2">
                  <Badge className="bg-secondary text-secondary-foreground text-xs">{t("games.adivinaLaPalabra.rules.present")}</Badge>
                  <span className="text-xs">{t("games.adivinaLaPalabra.rules.presentDesc")}</span>
                </div>
                <div className="flex items-center gap-2">
                  <Badge className="bg-muted text-muted-foreground text-xs">{t("games.adivinaLaPalabra.rules.absent")}</Badge>
                  <span className="text-xs">{t("games.adivinaLaPalabra.rules.absentDesc")}</span>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Advertisement below "Cómo Jugar" on desktop */}
          <InContentAd className="w-full" compact />

          {/* Game Shortcuts - Always show on desktop */}
          <GameShortcuts currentGame="adivina-la-palabra" />
        </div>
      </div>

      {/* Mobile Layout */}
      <div className="lg:hidden min-h-[calc(100dvh-64px)] flex flex-col">
        {/* Advertisement above game board on mobile */}
        <InContentAd className="w-full" compact />

        <div className="flex-1 flex flex-col py-2 overflow-y-auto">
          <div className="w-full py-4">
            {renderGameContent()}

            {/* Game Shortcuts - Mobile */}
            {(gameState.gameStatus === 'won' || gameState.gameStatus === 'lost') && (
              <GameShortcuts currentGame="adivina-la-palabra" />
            )}
          </div>
        </div>

        {gameState.gameStatus === 'playing' && (
          <div className="bg-background border-t p-1">
            <div className="space-y-1 w-full">
              {keyboard.map((row, rowIndex) => (
                <div key={rowIndex} className="flex gap-1">
                  {row.map((key) => {
                    const state = key.length === 1 ? getKeyState(key) : 'unused'
                    const isCheckKey = key === 'CHECK'
                    const isBackspaceKey = key === 'BACKSPACE'

                    return (
                      <Button
                        key={key}
                        variant="outline"
                        size="sm"
                        className={`flex-1 ${isCheckKey || isBackspaceKey ? 'flex-[2]' : ''} h-12 p-1 text-base font-bold rounded-md ${getKeyColor(state)} ${isCheckKey ? 'bg-primary hover:bg-primary/90 text-primary-foreground border-primary' : ''
                          }`}
                        onClick={() => handleKeyPress(key)}
                        disabled={gameState.gameStatus !== 'playing' || (isCheckKey && gameState.currentAttempt.join('').length !== wordLength)}
                      >
                        {isCheckKey ? '✓' : isBackspaceKey ? '⌫' : key}
                      </Button>
                    )
                  })}
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Modal de primera vez - Solo móvil */}
      <Dialog open={showFirstTimeModal} onOpenChange={setShowFirstTimeModal}>
        <DialogContent className="max-w-sm mx-auto mt-20">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Keyboard className="h-5 w-5" />
              {t("games.adivinaLaPalabra.howToPlay")}
            </DialogTitle>
            <DialogDescription className="text-left space-y-3">
              <p className="text-sm">{t("games.adivinaLaPalabra.rules.main")}</p>
              <div className="space-y-2">
                <div className="flex items-center gap-2">
                  <Badge className="bg-primary text-primary-foreground text-xs">{t("games.adivinaLaPalabra.rules.correct")}</Badge>
                  <span className="text-xs">{t("games.adivinaLaPalabra.rules.correctDesc")}</span>
                </div>
                <div className="flex items-center gap-2">
                  <Badge className="bg-secondary text-secondary-foreground text-xs">{t("games.adivinaLaPalabra.rules.present")}</Badge>
                  <span className="text-xs">{t("games.adivinaLaPalabra.rules.presentDesc")}</span>
                </div>
                <div className="flex items-center gap-2">
                  <Badge className="bg-muted text-muted-foreground text-xs">{t("games.adivinaLaPalabra.rules.absent")}</Badge>
                  <span className="text-xs">{t("games.adivinaLaPalabra.rules.absentDesc")}</span>
                </div>
              </div>
            </DialogDescription>
          </DialogHeader>
          <div className="flex justify-end pt-4">
            <Button onClick={handleCloseFirstTimeModal}>
              {t("navigation.gotIt")}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </GameLayout>
  )
} 